// ◉ xlVISION · Powering Business Together!
// © ExcelVision, Lda. — Braga, Portugal
// ( ·:·●●·:· ) xlVISION | Powering Business Together!
//
// ============================================================================
// O TOM DE UM PLANO — seis, fechados, e o NOME nunca é a cor
// ============================================================================
//
// 📗 **Cliente, 2026-09-10:** *«por uma distinção entre planos… por exemplo uma cor de fundo,
//    um título no cabeçalho a dizer o plano activo etc. E também pode ser aqui a selecção de
//    cores de fundo para o plano»*.
//
// ---------------------------------------------------------------------------
// 🔴🔴 O QUE SE GUARDA É O NOME DO TOM, NUNCA UM `#rrggbb` — e é a decisão central
// ---------------------------------------------------------------------------
//
//   Um selector de cor livre parece a resposta óbvia e é a errada aqui, por **três** razões
//   medidas e não opinadas:
//
//   1. ⚠️⚠️ **O portão do contraste é uma IGUALDADE e está no tecto: 197 de 197.** Ele mede
//      todo o par tinta/fundo da folha em Chrome sem cabeça, nos **dois** temas, e falha o
//      *build* à primeira tinta nova abaixo do AA. Um `#rrggbb` escolhido por uma pessoa na
//      Gestão **não passa por portão nenhum** — nasce depois do *build*. O que se guardasse
//      seria a garantia a desaparecer: texto ilegível sem nada o denunciar.
//   2. **Um tom tem de existir nos DOIS temas.** `#e8f0ff` é um fundo claro simpático e é
//      quase branco no tema escuro. Uma cor escolhida à mão tem uma definição; um tom tem
//      duas, e é a folha que as dá.
//   3. **Regra 3 e P-30/P-32:** a cor é **reforço**, e a identidade é o **nome** — que já está
//      no cabeçalho ao lado. É a mesma regra da sigla do cliente contra a cor no mapa.
//
//   ➡️ Logo o que viaja da base para o ecrã é `'azul'`, e é o CSS que decide o que isso é em
//   cada tema. 📌 **A consequência que interessa: os tons são CSS estático**, logo o portão do
//   contraste mede-os como mede tudo o resto, e um tom que passe hoje passa sempre.
//
// ---------------------------------------------------------------------------
// ⚠️⚠️ PORQUE CINCO E NÃO SEIS — a paleta segura é MENOR do que parece
// ---------------------------------------------------------------------------
//
//   São **matizes** separados e não graus do mesmo azul: a folha de Excel que este sistema
//   substitui tinha **3 colisões de cor medidas e 6 verdes distintos**, e é o erro que o
//   `CLAUDE.md` manda não herdar.
//
//   🔴 **E o lado quente está PROIBIDO, porque já quer dizer outra coisa.** Medido nos tokens
//   existentes: `--estado-critico: #d03b3b` (vermelho), `--estado-aviso: #fab219` (**âmbar**),
//   `--estado-bom: #0ca30c` (verde). Um plano âmbar ou cor de tijolo dizia **«isto está mal»**
//   a um ecrã inteiro que está bem — a família **«vestir a roupa de outra coisa»**, que neste
//   projecto já pôs uma barra a tapar 54 px do cabeçalho e uma zebra a vestir-se de faixa de
//   aviso, esta última hoje mesmo.
//
//   ⚠️ **Escrevi primeiro SEIS, com `ambar` e `terra` lá dentro — e os dois eram exactamente
//   essa colisão.** O âmbar é o `--estado-aviso` à letra; o terra, num traço de 2 px, não se
//   distingue do crítico com segurança. 📌 Tirados, sobram as famílias frias mais o neutro, e
//   **cinco é o que resta depois de tirar o que mente**. Preferi cinco honestos a seis com um
//   a dizer o que não é — e cinco chegam: os planos contam-se pelos dedos de uma mão.
//
// ---------------------------------------------------------------------------
// 🔴 UMA SÓ FORMULAÇÃO, TRÊS LEITORES
// ---------------------------------------------------------------------------
//
//   · a migração `080` — o `CHECK` que faz a base recusar em voz alta;
//   · `apps/api/src/gestao.ts` — o dropdown da Gestão, por `sqlDosTons()`;
//   · `apps/web/src/mapa/mapa.css` — os tons, dois por nome (claro e escuro).
//
//   ⚠️ Os três **têm de concordar**, e há guarda a exigi-lo: `tons-do-plano.test.ts` lê o SQL
//   da migração e o CSS e compara-os com esta lista. *«Duas formulações divergem, e a que
//   divergir é a que ninguém corre»* — aqui a que divergisse dava um plano com um tom que a
//   base aceita e o ecrã não sabe pintar, ou o contrário: um dropdown com uma opção que a base
//   recusa com `23514` na cara de quem gravou.
// ============================================================================
/**
 * 🔴 Os cinco tons, fechados.
 *
 * ⚠️ **Acrescentar um exige as três pontas no MESMO *commit*** — a migração (um `CHECK` novo),
 * esta lista, e o par de cores no CSS. A guarda recusa qualquer uma em falta, e é isso que
 * impede o caso mau: um tom gravável que o ecrã não pinta, e que se lê como plano sem cor.
 *
 * ⚠️⚠️ **E exige refazer a conta do contraste**, que não é opinião: `tons-do-plano.test.ts`
 * calcula a tinta sobre o fundo de cada tom **nos dois temas** e exige 4,5:1. Medido a
 * 2026-09-10, as dez combinações caem entre **5,97:1 e 7,16:1**.
 */
export const TONS_DO_PLANO = [
    { nome: 'azul', rotulo: 'azul' },
    { nome: 'turquesa', rotulo: 'turquesa' },
    { nome: 'violeta', rotulo: 'violeta' },
    { nome: 'magenta', rotulo: 'magenta' },
    { nome: 'ardosia', rotulo: 'ardósia' },
];
/** Só os nomes, que é o que a base guarda. */
export const NOMES_DOS_TONS = TONS_DO_PLANO.map((t) => t.nome);
/**
 * Este nome é um tom conhecido?
 *
 * ⚠️ **`null` e `''` respondem `false`, e isso é «sem tom» e não «tom inválido»** — um plano
 * sem cor escolhida é o caso normal, e o ecrã trata-o como tal em vez de o assinalar.
 */
export function eTomConhecido(nome) {
    return typeof nome === 'string' && NOMES_DOS_TONS.includes(nome);
}
/**
 * O predicado do `CHECK`, tal como a migração o escreve.
 *
 * ⚠️ **Existe para a GUARDA o poder comparar com o SQL real**, e não para a migração o chamar:
 * uma migração aplicada é **imutável** e não pode depender de código que muda debaixo dela.
 * 📌 É a mesma disciplina do resumo `sha256` — o SQL é a verdade, e isto é como se lê.
 */
export function predicadoDoCheck() {
    return `cor IS NULL OR cor IN (${NOMES_DOS_TONS.map((n) => `'${n}'`).join(', ')})`;
}
// ⚠️⚠️ **AQUI ESTEVE UM `sqlDosTons()`, E TIRÁ-LO É A LIÇÃO QUE VALE A PENA GUARDAR.**
//
//   Escrevi-o para alimentar o dropdown da Gestão por `refere: 'tom_do_plano'`, com um
//   `SELECT * FROM (VALUES …)`. 📌 **Inventei um mecanismo que já existia:** o `gestao.ts`
//   distingue, por extenso, o `refere` («escolhe uma linha de uma tabela») do `valoresFixos`
//   («escolhe um dos valores de um `CHECK`») — e isto é literalmente o segundo caso.
//
//   ✅ **Quem o apanhou foi o compilador**, porque a união do `refere` é **fechada**:
//   `Type '"tom_do_plano"' is not assignable to '"plano" | "motorista" | …'`. Uma união
//   fechada fez, de graça, o trabalho de uma revisão — e é o argumento a favor de as manter
//   fechadas mesmo quando dá jeito abri-las.
//
//   ⚠️ E a função saiu em vez de ficar «para o caso de»: sem consumidor era código morto, e a
//   regra 3 do `CLAUDE.md` manda limpar o que as minhas próprias alterações deixam órfão.
//# sourceMappingURL=tons-do-plano.js.map